import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { AuthLayout } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getGuestCart } from "@/lib/guestCart";

function passwordRules(pw) {
  return {
    len: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
}

export default function SignupPage() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", mobile: "",
    password: "", confirm: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const rules = passwordRules(form.password);
  const allRules = Object.values(rules).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allRules) {
      setError("Password does not meet all requirements.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!/^\+?[0-9\-\s]{6,20}$/.test(form.mobile)) {
      setError("Please enter a valid mobile number.");
      return;
    }
    setLoading(true);
    const { confirm, ...payload } = form;
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created — welcome to Steward!");
      // If guest had a cart, send them straight to checkout — merge already
      // happened inside AuthContext.register() and the toast lists what carried over.
      const hadCartBeforeMerge = getGuestCart().length;
      // NOTE: mergeGuestCart clears steward_guest_cart, so we check it BEFORE the effect.
      // Since register() awaits merge before returning, guest cart is already empty here.
      // Instead, we use the "steward_checkout_redirect" flag from cart page as source of truth.
      const wantsCheckout = localStorage.getItem("steward_checkout_redirect") === "1";
      if (wantsCheckout || hadCartBeforeMerge) {
        localStorage.removeItem("steward_checkout_redirect");
        navigate("/cart", { replace: true });
      } else {
        navigate("/app/learner", { replace: true });
      }
    } else {
      setError(res.error || "Registration failed.");
    }
  };

  const RuleRow = ({ ok, label }) => (
    <li className={`flex items-center gap-1.5 ${ok ? "text-[#329112]" : "text-slate-500"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-[#329112]" : "bg-slate-300"}`} />
      {label}
    </li>
  );

  return (
    <AuthLayout
      title="Create your learner account"
      subtitle="Self-signups are onboarded as learners. Hospitals create staff accounts via the admin console."
      hint={<>Already have an account? <Link to="/login" data-testid={AUTH.signupToLogin} className="font-semibold text-[#101876] hover:underline">Log in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid={AUTH.signupRoot}>
        {error && <Alert variant="destructive" data-testid={AUTH.signupError}><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">First name</Label>
            <Input required value={form.first_name} onChange={set("first_name")} data-testid={AUTH.signupFirst} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Last name</Label>
            <Input required value={form.last_name} onChange={set("last_name")} data-testid={AUTH.signupLast} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</Label>
          <Input type="email" required value={form.email} onChange={set("email")} data-testid={AUTH.signupEmail} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Mobile</Label>
          <Input required placeholder="+91 98765 43210" value={form.mobile} onChange={set("mobile")} data-testid={AUTH.signupMobile} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Password</Label>
          <Input type="password" required value={form.password} onChange={set("password")} data-testid={AUTH.signupPassword} />
          <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            <RuleRow ok={rules.len} label="8+ characters" />
            <RuleRow ok={rules.upper} label="1 uppercase" />
            <RuleRow ok={rules.lower} label="1 lowercase" />
            <RuleRow ok={rules.digit} label="1 number" />
            <RuleRow ok={rules.special} label="1 special char" />
          </ul>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Confirm password</Label>
          <Input type="password" required value={form.confirm} onChange={set("confirm")} data-testid={AUTH.signupConfirm} />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#329112] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#256b0d] disabled:opacity-70"
          data-testid={AUTH.signupSubmit}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {loading ? "Creating account…" : "Create account"}
        </button>
      </form>
    </AuthLayout>
  );
}
